'use client';

import React from 'react';

interface SystemElement {
  number: string;
  title: string;
  description: string;
}

const SystemOverview = () => {
  const elements: SystemElement[] = [
    {
      number: '01',
      title: 'Leadership Alignment',
      description: 'Establishing shared understanding, commitment, and accountability at the executive level for leading continuous AI-driven change with stewardship and confidence.',
    },
    {
      number: '02',
      title: 'Operating Model Adaptation',
      description: 'Redesigning structures, processes, and governance mechanisms to enable responsible scaling and continuous evolution of AI capabilities.',
    },
    {
      number: '03',
      title: 'Workforce Enablement',
      description: 'Building organisational capability, confidence, and readiness to work effectively with AI — extending beyond skills training to role design and cultural adaptation.',
    },
    {
      number: '04',
      title: 'Adoption Intelligence',
      description: 'Measuring, monitoring, and interpreting adoption patterns to guide leadership decisions and interventions with clarity and precision.',
    },
    {
      number: '05',
      title: 'Continuous Evolution',
      description: 'Embedding systems and leadership practices that sustain transformation momentum and enable organisations to adapt as AI capabilities advance.',
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-bold font-headline text-[#0A2540] mb-6 leading-tight">
              The AI Change Leadership System™
            </h2>
            <p className="text-xl text-gray-700 font-body leading-relaxed max-w-3xl">
              A cross-sector reference model defining best practice for AI leadership maturity. This framework enables assessment, benchmarking, and recognition of organisations that demonstrate the capability to scale AI adoption with governance, stewardship, and accountability.
            </p>
          </div>

          {/* System elements */}
          <div className="space-y-6">
            {elements.map((element, index) => (
              <div key={index} className="bg-gray-50 border-2 border-gray-200 p-8 hover:border-[#0A2540] transition-colors duration-300">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0 w-16 h-16 bg-[#0A2540] flex items-center justify-center">
                    <span className="text-2xl font-bold font-headline text-white">{element.number}</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold font-headline text-[#0A2540] mb-3">
                      {element.title}
                    </h3>
                    <p className="text-lg text-gray-700 font-body leading-relaxed">
                      {element.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* System statement */}
          <div className="mt-16 p-8 bg-gray-50 border-2 border-gray-200">
            <p className="text-lg text-gray-800 font-body leading-relaxed">
              The AI Change Leadership System™ is the foundation of the AI Change Leader Standard. It defines emerging best practices, leadership capabilities, and maturity expectations that enable organisations to lead continuous transformation at the pace AI evolves—with clarity, confidence, and responsible stewardship.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SystemOverview;